import { createApp } from '@backstage/frontend-defaults';
import {
  createFrontendModule,
  createApiRef,
  configApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  ApiBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import { ThemeBlueprint, SignInPageBlueprint } from '@backstage/plugin-app-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import Brightness2Icon from '@material-ui/icons/Brightness2';
import { navModule } from './modules/nav';
import { rootenyTheme } from './theme/rootenyTheme';
import './theme/rooteny.css';

const zohoAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>().with({
  id: 'auth.zoho',
});

const zohoAuthApi = ApiBlueprint.make({
  name: 'zoho',
  params: defineParams =>
    defineParams({
      api: zohoAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          provider: {
            // Must stay 'oidc' to match the backend OIDC strategy
            id: 'oidc',
            title: 'Zoho',
            icon: () => null,
          },
          // Zoho IbyraHub OIDC app does not expose a `groups` scope (Headlamp's app does).
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props =>
      (
        <SignInPage
          {...props}
          providers={[
            {
              id: 'zoho-auth-provider',
              title: 'Zoho',
              message: 'Sign in with Rooteny Zoho Directory',
              apiRef: zohoAuthApiRef,
            },
            // Local MVP / emergency fallback when guest provider is configured
            'guest',
          ]}
        />
      ),
  },
});

const rootenyThemeExtension = ThemeBlueprint.make({
  name: 'rooteny',
  params: {
    theme: {
      id: 'rooteny',
      title: 'Rooteny',
      variant: 'dark',
      icon: <Brightness2Icon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={rootenyTheme} children={children} />
      ),
    },
  },
});

const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [rootenyThemeExtension],
});

const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [zohoAuthApi, signInPage],
});

export default createApp({
  features: [catalogPlugin, navModule, themeModule, authModule],
});
